import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { APP_REGISTRY } from '@/gateway/apps';
import { WorkspaceShell } from '@/gateway/WorkspaceShell';
import { AppShellLayout } from '@/gateway/layout/AppShellLayout';
import { AppRoute } from '@/gateway/AppRoute';
import { ShellRoot } from '@/gateway/ShellRoot';
import { DocumentMeta } from '@/gateway/DocumentMeta';
import { RequireApp } from '@/gateway/guards/RequireApp';
import { RequireAuth } from '@/gateway/guards/RequireAuth';
import { RequireUnauthed } from '@/gateway/guards/RequireUnauthed';
import { AuthShell } from '@/gateway/layouts/AuthShell';
import { OnboardingShell } from '@/gateway/layouts/OnboardingShell';
import { LoginRoute } from '@/platform/auth/LoginRoute';
import { ForgotPasswordPage } from '@/platform/auth/ForgotPasswordPage';
import { SignupPage } from '@/platform/auth/SignupPage';
import { VerifyOtpPage } from '@/platform/auth/VerifyOtpPage';
import { SignupWorkspacePage } from '@/platform/auth/SignupWorkspacePage';
import { OnboardingEntry } from '@/platform/onboarding/OnboardingEntry';
import { RequireOnboardingComplete } from '@/gateway/guards/RequireOnboardingComplete';
import { RequireSetupComplete } from '@/gateway/guards/RequireSetupComplete';
import { RequireWorkspaceOwner } from '@/gateway/guards/RequireWorkspaceOwner';
import { InvitePreviewPage } from '@/platform/invites/InvitePreviewPage';
import { PasswordResetRequiredPage } from '@/platform/auth/PasswordResetRequiredPage';
import { EvaluationLinkPage } from '@/platform/pms/EvaluationLinkPage';
import { ManagerEvaluationLinkPage } from '@/platform/pms/ManagerEvaluationLinkPage';
import { PublicBookingRoute } from '@/platform/booking/PublicBookingRoute';
import { EvaluationStandalonePage } from '@/platform/pms/EvaluationStandalonePage';
import { ProfilePage } from '@/platform/profile/ProfilePage';
import { ColleagueProfilePage } from '@/platform/profile/ColleagueProfilePage';
import { DataAccessPage } from '@/platform/data-access/DataAccessPage';
import { ConsentManagePage } from '@/platform/consent/ConsentManagePage';
import { OAuthConsentPage } from '@/platform/oauth/OAuthConsentPage';
import { AppLauncher } from '@/gateway/AppLauncher';
import { ClarixHome } from '@clarix/ui-shared';
// Workspace-admin pages live in the apps/workspace Module Federation remote
// now — registered via APP_REGISTRY and lazy-loaded under /workspace/*. The
// /setup wizard also moved into the workspace remote; it is re-wired as a lazy
// MF load in the shell-host step (MF Task 7).

const DEFAULT_APP_SLUG = APP_REGISTRY[0]?.slug ?? 'projects';

export const router = createBrowserRouter(
  createRoutesFromElements(
    // Pathless layout route: matches every tier below, so ShellRoot's chrome
    // mounts once for the whole app rather than per tier.
    <Route element={<ShellRoot />}>
      {/*
        TIER 0 — PLATFORM ADMIN CONSOLE: REMOVED FROM THIS SHELL.

        The console used to mount here as the `clarixAdmin` federated remote at
        /admin/*. It is now a separate deployed application
        (clarix-admin-frontend) on its own origin, talking to its own backend
        (clarix-admin-backend) — not a remote of this shell, and not proxied by
        the gateway. Do not re-add a route: /admin on this origin should 404.
      */}

      {/*
        DEVELOPER TOOL — PUBLIC, NO AUTH, NO WORKSPACE.
        Client-side payload encrypt/decrypt debugger. Pure Web Crypto; the key is
        typed in by the user and nothing leaves the browser, so it needs no token
        or workspace context — it must work even while signed out and in prod, to
        debug captured production payloads. Not in APP_REGISTRY (never shown in
        the app switcher).
      */}
      <Route path="/data-access" element={<DataAccessPage />} />

      {/*
        DPDP CONSENT SELF-SERVICE — PUBLIC, NO AUTH, NO WORKSPACE.
        The landing page for the "Manage or withdraw consent" button in a
        consent-notice email (DPDP §6(4)). The visitor is a DATA PRINCIPAL — an
        external contact with no Clarix account — so this cannot sit under
        RequireAuth (no session), WorkspaceShell (no workspace) or the CRM
        remote at /crm/* (no app grant). Authorisation is the signed token in
        the query string, verified by crm-api.

        `/consent/revoke` is an ALIAS kept for the id-based links already sitting
        in recipients' inboxes. Those carry no token, so the page renders its
        "this link is out of date" state and points at the DPO — the correct
        outcome, and better than the 404-into-/login they hit before.

        Not in APP_REGISTRY: never shown in the app switcher.
      */}
      <Route path="/consent/manage" element={<ConsentManagePage />} />
      <Route path="/consent/revoke" element={<ConsentManagePage />} />

      {/*
        TIER 1 — PUBLIC AUTH (no token, no workspace).
        Bounce away if already signed in.
      */}
      <Route element={<AuthShell />}>
        <Route element={<RequireUnauthed />}>
          {/* Apex has no workspace, so it cannot host a login form — it asks
              which workspace first. On a workspace subdomain this IS the login.
              One path, so every existing /login link and redirect still works. */}
          <Route path="/login" element={<LoginRoute />} />
          {/*
            NO '/workspace' route here. It is the WORKSPACE APP's home
            (APP_REGISTRY mounts `workspace/*` in tier 4, and the app's rail
            links '/workspace' as Overview). A static '/workspace' declared here
            out-ranks that splat in React Router's specificity ordering, so it
            shadowed the app: clicking "Workspace" in the launcher landed on this
            public picker, RequireUnauthed bounced the signed-in user back to
            '/', and the card looked dead. Every other app was fine — none of
            them had a tier-1 twin.

            The picker needs no path of its own: LoginRoute renders it at
            '/login' whenever there is no workspace slug, which is the
            one-path-not-two design its own docstring argues for.
          */}
          {/* Public: a user who forgot their password cannot hold a JWT. */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/verify-otp" element={<VerifyOtpPage />} />
          {/* Step 3 — workspace + owner details, pre-auth. Carries { email,
              emailVerificationToken } via router state from VerifyOtpPage;
              guards and bounces to /signup if arrived without it. */}
          <Route path="/signup/workspace" element={<SignupWorkspacePage />} />
        </Route>
      </Route>

      {/*
        TIER 2 — PUBLIC INVITE PREVIEW.
        No auth gate; the page itself handles signed-in vs signed-out flows.
      */}
      <Route element={<AuthShell />}>
        <Route path="/invites/:token" element={<InvitePreviewPage />} />
      </Route>

      {/*
        FORCE-RESET LANDING — GUARD-LESS.
        Reachable whether or not the user has a session: at login (no token
        issued) and mid-session (the api-client interceptor clears the session
        before redirecting here). No RequireUnauthed (the user may have a
        partial session object) and no RequireAuth (they have no valid token).
      */}
      <Route element={<AuthShell />}>
        <Route path="/password-reset-required" element={<PasswordResetRequiredPage />} />
      </Route>

      {/*
        TIER 2b — PUBLIC EVALUATION LINK (PMS §12).
        No auth gate and NO AuthShell: the page renders its own full-screen
        chrome, the way the prototype's tokenised link does. The employee opens
        it from an invitation email, and the token in the URL is the credential —
        a login gate here would lock out every directory-only member, who has no
        Clarix account at all (WorkspaceMembership.user_id = null).
      */}
      <Route path="/pms/eval/:token" element={<EvaluationLinkPage />} />
      {/*
        The MANAGER's link (D-27) — same tier, same reasoning. `rm` is a literal
        segment before the token, so this and the route above differ in length
        and neither can shadow the other.
      */}
      <Route path="/pms/eval/rm/:token" element={<ManagerEvaluationLinkPage />} />

      {/*
        TIER 2c — PUBLIC BOOKING PAGE (Calendar contract v8 §B15.2). No auth gate, no
        AuthShell: an invitee has no account. A static `/book/` prefix never collides
        with apex paths or a slug, and `manage` is one segment shorter than a page link.
      */}
      <Route path="/book/:workspace/manage" element={<PublicBookingRoute />} />
      <Route path="/book/:workspace/:handle/:event" element={<PublicBookingRoute />} />

      {/*
        TIER 3 — AUTHENTICATED, NO WORKSPACE REQUIRED.
        Onboarding wizard.
      */}
      <Route element={<RequireAuth />}>
        {/*
          /onboarding works on both apex AND workspace subdomains. The
          OnboardingEntry component picks the right page based on hostname:
            - apex      → OnboardingPage (step 1: name + slug)
            - <slug>.*  → OnboardingWizardPage (steps 2-4 + branding)
          Each page handles its own redirect when onboarding is already
          complete or the user has the wrong number of workspaces.
        */}
        <Route element={<OnboardingShell />}>
          <Route path="/onboarding" element={<OnboardingEntry />} />
        </Route>

        {/*
          MCP OAuth consent — an AI app asked to act as this member. Signed in,
          but outside onboarding/setup/WorkspaceShell so nothing bounces the
          request away before the member answers it.
        */}
        <Route element={<AuthShell />}>
          <Route path="/oauth/consent" element={<OAuthConsentPage />} />
        </Route>

        {/*
          TIER 4 — AUTHENTICATED + WORKSPACE REQUIRED + ONBOARDING COMPLETE.
          WorkspaceShell calls /me and either renders the chrome or redirects.
          RequireOnboardingComplete (workspace subdomain only) bounces to
          /onboarding until onboarding_state=DONE.
        */}
        <Route element={<RequireOnboardingComplete />}>
          {/*
            Until setup_state=COMPLETE, the guard redirects everything except
            /setup to the chrome-free wizard at /setup. The wizard lives
            outside WorkspaceShell so it renders without TopBar/SidebarRail.
          */}
          <Route element={<RequireSetupComplete />}>
            {/*
            Chrome-free setup wizard — sibling of WorkspaceShell, owner-only.
            SetupPage lives in the workspace remote (clarixWorkspace/SetupPage); AppRoute
            lazy-loads it with Suspense + retry + AppErrorBoundary, so a remote
            load failure here is isolated just like any app route.
          */}
            <Route element={<RequireWorkspaceOwner />}>
              <Route
                path="/setup"
                element={
                  <AppRoute
                    appName="Setup"
                    // The setup wizard is served by workspace-api, so its health
                    // is the workspace app's health.
                    appSlug="workspace"
                    loader={() => import('clarixWorkspace/SetupPage')}
                  />
                }
              />
            </Route>
            <Route element={<WorkspaceShell />}>
              {/*
              App launcher ("Choose an app") — chrome-free. It sits directly
              under WorkspaceShell (so it still gets workspace validation) but
              OUTSIDE AppShellLayout, so it renders without TopBar/SidebarRail.
            */}
              <Route
                path="/"
                element={
                  <>
                    {/* The launcher is app-agnostic ("Choose an app"), so the
                        tab shows the generic Clarix brand — not whichever app
                        the user came from. */}
                    <DocumentMeta />
                    <AppLauncher />
                  </>
                }
              />

              {/*
                PMS evaluation forms — CHROME-FREE, same slot as the launcher.
                Authenticated and workspace-validated (inside WorkspaceShell) but
                OUTSIDE AppShellLayout, so no rail and no TopBar.

                They open in their own tab and are a long, focused document; the
                prototype gives them a bare page (`.linkpage`) and the tokenised
                link already renders that way. Routed here rather than inside the
                PMS remote because everything in `clarixPms/App` renders within
                AppShellLayout by construction.

                More specific than the `pms/*` splat below, so they win the match
                and every existing link keeps its URL.
              */}
              <Route
                path="/pms/my-team/:participantId"
                element={<EvaluationStandalonePage mode="observer" />}
              />
              <Route
                path="/pms/my-evaluations/:participantId"
                element={<EvaluationStandalonePage mode="self" />}
              />

              {/* Everything else renders inside the shared chrome. */}
              <Route element={<AppShellLayout />}>
                {/*
                Platform profile — chrome-wrapped page shared across every app
                (opened from the SidebarRail avatar). Lives in the shell, not in
                any remote, since it aggregates the user's access across all apps.
              */}
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/profile/:membershipId" element={<ColleagueProfilePage />} />

                {/*
                Home — ONE screen at ONE url, for every app. Shell-owned like
                /profile: it belongs to no app, so it carries no RequireApp and
                no app segment. Every `/<slug>/home` redirects here, and the
                rail follows the app you came through (AppShellLayout keeps the
                last app on app-less routes).
              */}
                <Route path="/home" element={<ClarixHome />} />

                {/*
                Registered apps. workspace, projects, crm live under
                apps/gateway/src/apps/* and are lazy-loaded into their own chunk
                under /<slug>/* (e.g. /workspace/*) via React.lazy.
              */}
                {APP_REGISTRY.map((app) => (
                  <Route
                    key={app.id}
                    path={`${app.slug}/*`}
                    element={
                      <RequireApp app={app}>
                        <DocumentMeta appName={app.name} favicon={app.favicon} />
                        <AppRoute appName={app.name} appSlug={app.slug} loader={app.loader} />
                      </RequireApp>
                    }
                  />
                ))}

                <Route path="*" element={<Navigate to={`/${DEFAULT_APP_SLUG}`} replace />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>,
  ),
);
